import { useMemo } from "react";
import { GfFlyerReviewComment } from "@swagger/typescript-fetch-goflyer";

/** One reply row in a thread, with depth for nested UI (1 = direct child of root). */
export interface ThreadReplyRow {
  comment: GfFlyerReviewComment;
  depth: number;
}

export interface ThreadView {
  root: GfFlyerReviewComment;
  replies: ThreadReplyRow[];
}

const toTime = (d: Date | string | undefined): number => {
  if (!d) return 0;
  const t = new Date(d as string).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** Depth-first: siblings sorted by time, then each branch under that parent. */
const collectReplyRows = (
  parentId: string,
  depth: number,
  repliesByParent: Map<string, GfFlyerReviewComment[]>,
): ThreadReplyRow[] => {
  const direct = (repliesByParent.get(parentId) ?? [])
    .slice()
    .sort((a, b) => toTime(a.createdAt) - toTime(b.createdAt));
  const out: ThreadReplyRow[] = [];
  for (const c of direct) {
    out.push({ comment: c, depth });
    out.push(...collectReplyRows(c.id, depth + 1, repliesByParent));
  }
  return out;
};

const latestActivityMs = (t: ThreadView): number =>
  Math.max(
    toTime(t.root.createdAt),
    ...t.replies.map((r) => toTime(r.comment.createdAt)),
  );

export const useCommentThreads = (
  comments: GfFlyerReviewComment[],
): ThreadView[] => {
  return useMemo(() => {
    const byId = new Map<string, GfFlyerReviewComment>();
    for (const c of comments) byId.set(c.id, c);

    const repliesByParent = new Map<string, GfFlyerReviewComment[]>();
    const roots: GfFlyerReviewComment[] = [];

    for (const c of comments) {
      const parentId = c.parentCommentId;
      if (parentId && byId.has(parentId)) {
        const bucket = repliesByParent.get(parentId) ?? [];
        bucket.push(c);
        repliesByParent.set(parentId, bucket);
      } else {
        roots.push(c);
      }
    }

    const threads: ThreadView[] = roots.map((root) => ({
      root,
      replies: collectReplyRows(root.id, 1, repliesByParent),
    }));

    threads.sort((a, b) => latestActivityMs(b) - latestActivityMs(a));

    return threads;
  }, [comments]);
};

export type AuthorLookup = (c: GfFlyerReviewComment) => { name: string };

export const threadMatchesSearch = (
  thread: ThreadView,
  needle: string,
  resolveAuthor: AuthorLookup,
): boolean => {
  const all: GfFlyerReviewComment[] = [
    thread.root,
    ...thread.replies.map((r) => r.comment),
  ];
  return all.some((c) => {
    if ((c.message || "").toLowerCase().includes(needle)) return true;
    if ((c.fieldKey || "").toLowerCase().includes(needle)) return true;
    const author = resolveAuthor(c).name.toLowerCase();
    return author.includes(needle);
  });
};
